import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, cmsApi, categoriesApi } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Package, Store, Users, ShoppingBag, Loader2,
  Settings, LayoutDashboard, Truck, Palette,
  Menu, X, FileSpreadsheet, Layers, UserCircle, Megaphone, Wallet, FileText,
  GalleryHorizontal, UserPlus,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import {
  OverviewSection, StoresSection, ProductsSection, OrdersSection,
  CustomersSection, AdsSection, DesignSection,
  FooterSection, ShippingSection, LegalTermsSection, SettingsSection, CategoriesSection,
  BannerEditForm, ProductEditForm, ByggFyndAdsSection, PartnerSection
} from './components';

const MENU_ITEMS = [
  { id: 'overview', label: 'Oversikt', icon: LayoutDashboard },
  { id: 'partners', label: 'Partners', icon: UserPlus },
  { id: 'byggfynd-ads', label: 'ByggFynd Annonser', icon: Megaphone },
  { id: 'ads', label: 'Partnerbanner', icon: GalleryHorizontal },
  { id: 'stores', label: 'Butiker', icon: Store },
  { id: 'products', label: 'Produkter', icon: Package },
  { id: 'orders', label: 'Ordrar', icon: ShoppingBag },
  { id: 'invoices', label: 'Fakturaunderlag', icon: FileSpreadsheet, link: '/admin/fakturaunderlag' },
  {
    id: 'payouts',
    label: 'Att betala ut',
    icon: Wallet,
    link: '/admin/fakturaunderlag?vy=utbetalning',
  },
  { id: 'customers', label: 'Kunder', icon: Users },
  { id: 'divider-1', divider: true, label: 'Utseende' },
  { id: 'design', label: 'Utseende', icon: Palette },
  { id: 'footer', label: 'Footer', icon: Palette },
  { id: 'categories', label: 'Kategorier', icon: Layers },
  { id: 'divider-2', divider: true, label: 'System' },
  { id: 'shipping', label: 'Frakt', icon: Truck },
  { id: 'legal-terms', label: 'Användarvillkor', icon: FileText },
  { id: 'settings', label: 'Installningar', icon: Settings },
];

export const AdminPage = () => {
  const navigate = useNavigate();
  const { token, isAdmin, isAuthenticated, loading: authLoading, user } = useAuth();
  const [activeSection, setActiveSection] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Data states
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);

  // Filters
  const [storeFilter, setStoreFilter] = useState('all');
  const [bannerFilter, setBannerFilter] = useState('all');
  const [customerSearch, setCustomerSearch] = useState('');

  // CMS & Settings State
  const [siteContent, setSiteContent] = useState(null);
  const [savingContent, setSavingContent] = useState(false);
  const [shippingSettings, setShippingSettings] = useState(null);
  const [savingShipping, setSavingShipping] = useState(false);
  const [legalTerms, setLegalTerms] = useState(null);
  const [savingLegal, setSavingLegal] = useState(false);
  const [bannerSettings, setBannerSettings] = useState(null);
  const [savingBanner, setSavingBanner] = useState(false);
  const [partnerSettings, setPartnerSettings] = useState(null);
  const [savingPartner, setSavingPartner] = useState(false);

  // Dialogs
  const [editBanner, setEditBanner] = useState(null);
  const [isBannerDialogOpen, setIsBannerDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);

  // Maintenance Mode
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [maintenanceTitle, setMaintenanceTitle] = useState('');
  const [maintenanceEta, setMaintenanceEta] = useState('');
  const [savingMaintenance, setSavingMaintenance] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) { toast.error('Du maste vara inloggad'); navigate('/logga-in'); return; }
    if (!authLoading && isAuthenticated && !isAdmin) { toast.error('Du har inte behorighet att se denna sida'); navigate('/'); }
  }, [isAdmin, isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAdmin && token) fetchData();
  }, [isAdmin, token, storeFilter, bannerFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, storesRes, productsRes, ordersRes, contentRes, shippingRes, legalRes, bannersRes, bannerSettingsRes, partnerSettingsRes, categoriesRes] = await Promise.all([
        adminApi.getStatistics(),
        adminApi.getStores(storeFilter !== 'all' ? { status: storeFilter } : {}),
        adminApi.getProducts(),
        adminApi.getOrders(),
        cmsApi.getContent().catch(() => ({ data: null })),
        adminApi.getShippingSettings().catch(() => ({ data: null })),
        adminApi.getLegalTerms().catch(() => ({ data: null })),
        adminApi.getBanners(bannerFilter !== 'all' ? { status: bannerFilter } : {}).catch(() => ({ data: { banners: [] } })),
        adminApi.getBannerSettings().catch(() => ({ data: null })),
        adminApi.getPartnerSettings().catch(() => ({ data: null })),
        categoriesApi.getAll().catch(() => ({ data: [] }))
      ]);
      setStatistics(statsRes.data); setStores(storesRes.data.stores); setProducts(productsRes.data.products);
      setOrders(ordersRes.data.orders); setSiteContent(contentRes.data);
      setShippingSettings(shippingRes.data); setLegalTerms(legalRes.data);
      setBanners(bannersRes.data?.banners || []);
      setBannerSettings(bannerSettingsRes.data);
      setPartnerSettings(partnerSettingsRes.data);
      setCategories(categoriesRes.data || []);
      setMaintenanceMode(contentRes.data?.maintenance_mode || false);
      setMaintenanceMessage(
        contentRes.data?.maintenance_message
        || 'Vi uppdaterar plattformen för att ge dig en ännu bättre upplevelse. Vänligen återkom snart!',
      );
      setMaintenanceTitle(contentRes.data?.maintenance_title || 'Underhållsarbete pågår');
      setMaintenanceEta(contentRes.data?.maintenance_eta || 'Några minuter');
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Kunde inte hamta data');
    } finally { setLoading(false); }
  };

  const fetchCustomers = async (search = '', role = 'all', status = 'all') => {
    try {
      const params = {};
      if (search) params.search = search;
      if (role && role !== 'all') params.role = role;
      if (status && status !== 'all') params.status = status;
      const res = await adminApi.getCustomers(params);
      setCustomers(res.data.customers || []);
    } catch { toast.error('Kunde inte hämta användare'); }
  };

  // Handlers
  const handleApproveStore = async (storeId) => { try { await adminApi.updateStore(storeId, { status: 'approved' }); toast.success('Butiken har godkants'); fetchData(); } catch { toast.error('Kunde inte godkanna butiken'); } };
  const handleSaveStoreBankgiro = async (storeId, bankgiro) => {
    await adminApi.updateStore(storeId, { bankgiro });
    toast.success('Bankgiro sparat');
    fetchData();
  };
  const handleApproveBanner = async (bannerId) => { try { await adminApi.updateBannerStatus(bannerId, 'approved'); toast.success('Annonsen har godkants'); fetchData(); } catch { toast.error('Kunde inte godkanna annonsen'); } };
  const handleRejectBanner = async (bannerId) => { if (!window.confirm('Ar du saker?')) return; try { await adminApi.updateBannerStatus(bannerId, 'rejected'); toast.success('Annonsen har avvisats'); fetchData(); } catch { toast.error('Kunde inte avvisa annonsen'); } };
  const handleMarkBannerPaid = async (bannerId) => { try { await adminApi.updateBannerPayment(bannerId, 'paid'); toast.success('Annonsen markerad som betald'); fetchData(); } catch { toast.error('Kunde inte uppdatera betalstatus'); } };
  const handleUpdateBanner = async (bannerId, data) => {
    try {
      const params = new URLSearchParams();
      if (data.title) params.append('title', data.title);
      if (data.image_url) params.append('image_url', data.image_url);
      if (data.link_url != null) params.append('link_url', data.link_url);
      await adminApi.updateBanner(bannerId, params.toString());
      toast.success('Annonsen har uppdaterats');
      setIsBannerDialogOpen(false);
      fetchData();
    } catch {
      toast.error('Kunde inte uppdatera annonsen');
    }
  };
  const handleDeleteBanner = async (bannerId) => { if (!window.confirm('Ar du saker?')) return; try { await adminApi.deleteBanner(bannerId); toast.success('Annonsen har tagits bort'); fetchData(); } catch { toast.error('Kunde inte ta bort annonsen'); } };
  const handleDeleteProduct = async (product) => { if (!window.confirm(`Ta bort "${product.title}"?`)) return; try { await adminApi.deleteProduct(product.id); toast.success('Produkten har tagits bort'); fetchData(); } catch { toast.error('Kunde inte ta bort produkten'); } };
  const handleUpdateProduct = async (productId, data) => { try { await adminApi.updateProduct(productId, data); toast.success('Produkten har uppdaterats'); setIsProductDialogOpen(false); setEditProduct(null); fetchData(); } catch { toast.error('Kunde inte uppdatera produkten'); } };
  const handleSaveContent = async () => { setSavingContent(true); try { await cmsApi.updateContent(siteContent); toast.success('Innehallet har sparats'); } catch { toast.error('Kunde inte spara innehallet'); } finally { setSavingContent(false); } };
  const handleSaveShipping = async () => { setSavingShipping(true); try { await adminApi.updateShippingSettings(shippingSettings); toast.success('Fraktinstallningarna har sparats'); } catch { toast.error('Kunde inte spara fraktinstallningarna'); } finally { setSavingShipping(false); } };
  const handleSaveLegalTerms = async () => {
    setSavingLegal(true);
    try {
      const res = await adminApi.updateLegalTerms({
        consumer_terms: legalTerms.consumer_terms,
        store_terms: legalTerms.store_terms,
      });
      setLegalTerms({
        consumer_terms: res.data.consumer_terms,
        store_terms: res.data.store_terms,
        consumer_terms_updated_at: res.data.consumer_terms_updated_at,
        store_terms_updated_at: res.data.store_terms_updated_at,
      });
      toast.success('Användarvillkor sparade');
    } catch {
      toast.error('Kunde inte spara användarvillkor');
    } finally {
      setSavingLegal(false);
    }
  };
  const handleSaveBannerSettings = async () => { setSavingBanner(true); try { await adminApi.updateBannerSettings(bannerSettings); toast.success('Banner-installningarna har sparats'); } catch { toast.error('Kunde inte spara banner-installningarna'); } finally { setSavingBanner(false); } };
  const handleSavePartnerSettings = async () => {
    setSavingPartner(true);
    try {
      const res = await adminApi.updatePartnerSettings(partnerSettings);
      setPartnerSettings(res.data);
      toast.success('Partner-sidor sparade');
    } catch {
      toast.error('Kunde inte spara partner-sidor');
    } finally {
      setSavingPartner(false);
    }
  };
  const maintenancePayload = () => ({
    maintenance_message: maintenanceMessage,
    maintenance_title: maintenanceTitle,
    maintenance_eta: maintenanceEta,
  });
  const handleToggleMaintenance = async () => {
    setSavingMaintenance(true);
    try {
      await cmsApi.updateContent({
        maintenance_mode: !maintenanceMode,
        ...maintenancePayload(),
      });
      setMaintenanceMode(!maintenanceMode);
      toast.success(maintenanceMode ? 'Underhållsläge avstängt' : 'Underhållsläge aktiverat');
    } catch {
      toast.error('Kunde inte ändra underhållsläge');
    } finally {
      setSavingMaintenance(false);
    }
  };
  const handleSaveMaintenanceTexts = async () => {
    setSavingMaintenance(true);
    try {
      await cmsApi.updateContent(maintenancePayload());
      toast.success('Underhållstexter sparade');
    } catch {
      toast.error('Kunde inte spara texterna');
    } finally {
      setSavingMaintenance(false);
    }
  };
  const handleToggleUserPause = async (userId) => { try { const res = await adminApi.toggleUserPause(userId); toast.success(res.data.message); fetchData(); fetchCustomers(customerSearch); } catch { toast.error('Kunde inte ändra status'); } };
  const handleToggleStorePause = async (storeId) => { try { const res = await adminApi.toggleStorePause(storeId); toast.success(res.data.message); fetchData(); } catch { toast.error('Kunde inte ändra status'); } };
  const handleDeleteUser = async (userId) => { try { await adminApi.deleteUser(userId); toast.success('Användare raderad'); fetchCustomers(customerSearch); } catch { toast.error('Kunde inte radera användare'); } };
  const handleDeleteStore = async (storeId) => { try { await adminApi.deleteStore(storeId); toast.success('Butik raderad'); fetchData(); } catch { toast.error('Kunde inte radera butik'); } };

  if (authLoading) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003B73]" /></div>;
  if (!isAdmin) return null;

  const overview = statistics.overview || {};
  const settingsData = statistics.settings || {};
  const trends = statistics.trends || {};
  const topStores = statistics.top_stores || [];
  const topCategories = statistics.top_categories || [];
  const pendingBanners = statistics?.pending_banners || 0;
  const pendingStores = overview.pending_stores || 0;

  const navTo = (section) => {
    setActiveSection(section);
    if (section === 'customers') fetchCustomers();
    if (section === 'ads') fetchData();
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'overview': return <OverviewSection overview={overview} settings={settingsData} trends={trends} topStores={topStores} topCategories={topCategories} pendingBanners={pendingBanners} onNavigate={navTo} maintenanceMode={maintenanceMode} />;
      case 'partners': return <PartnerSection partnerSettings={partnerSettings} setPartnerSettings={setPartnerSettings} onSaveSettings={handleSavePartnerSettings} savingSettings={savingPartner} onStoreCreated={fetchData} loading={loading} />;
      case 'byggfynd-ads': return <ByggFyndAdsSection />;
      case 'stores': return <StoresSection stores={stores} loading={loading} onApprove={handleApproveStore} onTogglePause={handleToggleStorePause} onDeleteStore={handleDeleteStore} onSaveBankgiro={handleSaveStoreBankgiro} />;
      case 'products': return <ProductsSection products={products} loading={loading} onEdit={(product) => { setEditProduct(product); setIsProductDialogOpen(true); }} onDelete={handleDeleteProduct} />;
      case 'orders': return <OrdersSection orders={orders} loading={loading} />;
      case 'customers': return <CustomersSection customers={customers} loading={loading} search={customerSearch} setSearch={setCustomerSearch} onSearch={fetchCustomers} onTogglePause={handleToggleUserPause} onDeleteUser={handleDeleteUser} />;
      case 'categories': return <CategoriesSection categories={categories} loading={loading} onRefresh={fetchData} />;
      case 'ads': return <AdsSection banners={banners} bannerSettings={bannerSettings} setBannerSettings={setBannerSettings} loading={loading} filter={bannerFilter} setFilter={setBannerFilter} onApprove={handleApproveBanner} onReject={handleRejectBanner} onMarkPaid={handleMarkBannerPaid} onEdit={(banner) => { setEditBanner(banner); setIsBannerDialogOpen(true); }} onDelete={handleDeleteBanner} onSaveSettings={handleSaveBannerSettings} savingBanner={savingBanner} onRefresh={fetchData} />;
      case 'design': return <DesignSection siteContent={siteContent} setSiteContent={setSiteContent} onSave={handleSaveContent} saving={savingContent} />;
      case 'footer': return <FooterSection siteContent={siteContent} setSiteContent={setSiteContent} onSave={handleSaveContent} saving={savingContent} />;
      case 'shipping': return <ShippingSection shippingSettings={shippingSettings} setShippingSettings={setShippingSettings} onSave={handleSaveShipping} saving={savingShipping} />;
      case 'legal-terms': return <LegalTermsSection legalTerms={legalTerms} setLegalTerms={setLegalTerms} onSave={handleSaveLegalTerms} saving={savingLegal} />;
      case 'settings': return (
        <SettingsSection
          maintenanceMode={maintenanceMode}
          maintenanceTitle={maintenanceTitle}
          setMaintenanceTitle={setMaintenanceTitle}
          maintenanceMessage={maintenanceMessage}
          setMaintenanceMessage={setMaintenanceMessage}
          maintenanceEta={maintenanceEta}
          setMaintenanceEta={setMaintenanceEta}
          onToggleMaintenance={handleToggleMaintenance}
          onSaveMaintenanceTexts={handleSaveMaintenanceTexts}
          savingMaintenance={savingMaintenance}
        />
      );
      default: return null;
    }
  };

  const visibleMenuItems = MENU_ITEMS.filter(m => !m.divider);

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="admin-page">
      {/* Mobile header */}
      <div className="lg:hidden bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-2">
          <span className="text-lg font-black text-[#0F172A]">Admin</span>
          <span className="text-xs bg-[#003B73] text-white px-2 py-0.5 rounded-full font-bold">ByggFynd</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:flex flex-col w-[260px] bg-white border-r border-gray-100 min-h-screen sticky top-0" data-testid="admin-sidebar">
          {/* Sidebar header */}
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#003B73] flex items-center justify-center">
                <UserCircle className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-[#0F172A] truncate">{user?.name || 'Admin'}</p>
                <p className="text-[11px] text-gray-400 truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Pending alerts */}
          {(pendingBanners > 0 || pendingStores > 0) && (
            <div className="px-4 py-2.5 bg-orange-50 border-b border-orange-100">
              {pendingStores > 0 && <p className="text-xs text-orange-700 font-semibold">{pendingStores} butik{pendingStores > 1 ? 'er' : ''} väntar</p>}
              {pendingBanners > 0 && <p className="text-xs text-orange-700 font-semibold">{pendingBanners} annons{pendingBanners > 1 ? 'er' : ''} väntar</p>}
            </div>
          )}

          <nav className="flex-1 px-3 py-4 overflow-y-auto">
            {MENU_ITEMS.map((item) => {
              if (item.divider) {
                return <p key={item.id} className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-6 mb-2 px-3">{item.label}</p>;
              }
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              const pendingBannerCount =
                item.id === 'stores' ? pendingStores : item.id === 'ads' ? pendingBanners : 0;
              const hasBadge = pendingBannerCount > 0;
              return (
                <button
                  key={item.id}
                  onClick={() => { if (item.link) { navigate(item.link); return; } navTo(item.id); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-150 mb-0.5 ${
                    isActive
                      ? 'bg-[#003B73] text-white shadow-md shadow-blue-900/10'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  data-testid={`menu-${item.id}`}
                >
                  <Icon className={`h-[18px] w-[18px] flex-shrink-0 ${isActive ? 'text-blue-200' : 'text-gray-400'}`} />
                  <span className="font-medium text-[13px]">{item.label}</span>
                  {hasBadge && <span className="ml-auto bg-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{pendingBannerCount}</span>}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setMobileMenuOpen(false)}>
            <div className="bg-white w-72 h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-5 border-b border-gray-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#003B73] flex items-center justify-center">
                  <UserCircle className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold">{user?.name || 'Admin'}</p>
                  <p className="text-[11px] text-gray-400 truncate">{user?.email}</p>
                </div>
              </div>
              <nav className="p-3">
                {visibleMenuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeSection === item.id;
                  const pendingBannerCount =
                    item.id === 'stores' ? pendingStores : item.id === 'ads' ? pendingBanners : 0;
                  const hasBadge = pendingBannerCount > 0;
                  return (
                    <button key={item.id} onClick={() => { if (item.link) { navigate(item.link); return; } navTo(item.id); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left mb-0.5 ${isActive ? 'bg-[#003B73] text-white' : 'text-gray-600'}`}>
                      <Icon className={`h-[18px] w-[18px] ${isActive ? 'text-blue-200' : 'text-gray-400'}`} />
                      <span className="font-medium text-[13px]">{item.label}</span>
                      {hasBadge && <span className="ml-auto bg-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{pendingBannerCount}</span>}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 p-5 lg:p-8">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h2 className="text-2xl font-black text-[#0F172A] tracking-tight">{visibleMenuItems.find(m => m.id === activeSection)?.label || 'Admin'}</h2>
            </div>
            {renderSection()}
          </div>
        </main>
      </div>

      {/* Dialogs */}
      <Dialog open={isBannerDialogOpen} onOpenChange={setIsBannerDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Redigera annons</DialogTitle></DialogHeader>
          {editBanner && <BannerEditForm banner={editBanner} onSave={(data) => handleUpdateBanner(editBanner.id, data)} onCancel={() => setIsBannerDialogOpen(false)} />}
        </DialogContent>
      </Dialog>

      <Dialog open={isProductDialogOpen} onOpenChange={setIsProductDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Redigera produkt</DialogTitle></DialogHeader>
          {editProduct && <ProductEditForm product={editProduct} onSave={(data) => handleUpdateProduct(editProduct.id, data)} onCancel={() => { setIsProductDialogOpen(false); setEditProduct(null); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
